import { Link } from "react-router-dom";
import VendorDirectoryPermissionGate from "../../casamap/admin/VendorDirectoryPermissionGate";
import { VENDOR_DIRECTORY_PERMISSIONS } from "../../../auth/casamapaVendorDirectoryPermissions";
import { formatStatusLabel } from "../../../utils/clientPortalFormat";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.candidates";
const DETAIL_PREFIX = `${COPY_PREFIX}.detail`;
const DUPLICATES_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.duplicates";

function confidenceBadgeClass(band) {
  if (band === "exact" || band === "very_likely") return "cvd-badge--published";
  if (band === "conflict") return "cvd-badge--suspended";
  if (band === "likely") return "cvd-badge--warning";
  return "cvd-badge--draft";
}

function matchTypePlainLabel(t, matchType) {
  return t(`${DETAIL_PREFIX}.matchTypePlain.${matchType}`, {}, formatStatusLabel(matchType));
}

function getPeerRecordLabel(match, candidateId) {
  if (match.matchType === "candidate_vendor") {
    if (match.candidateId === candidateId) {
      return match.recordBName || match.vendorId;
    }
    return match.recordAName || match.vendorId;
  }

  if (match.candidateId === candidateId) {
    return match.recordBName || match.candidateIdB;
  }
  if (match.candidateIdB === candidateId) {
    return match.recordAName || match.candidateId;
  }
  return match.recordBName || match.recordAName || "—";
}

function getPeerRecordLink(match, candidateId, serviciosBase) {
  if (match.matchType === "candidate_vendor") {
    const vendorId = match.candidateId === candidateId ? match.vendorId : match.vendorId || match.vendorIdB;
    return vendorId ? `${serviciosBase}/proveedores/${vendorId}` : null;
  }

  const peerCandidateId =
    match.candidateId === candidateId ? match.candidateIdB : match.candidateIdB === candidateId ? match.candidateId : null;
  return peerCandidateId ? `${serviciosBase}/candidatos/${peerCandidateId}` : null;
}

function ContextSection({ icon, title, subtitle, tone = "default", children }) {
  return (
    <section className={`cvd-review-context cvd-review-context--${tone}`}>
      <header className="cvd-review-context__header">
        <span className="cvd-review-context__icon" aria-hidden="true">
          {icon}
        </span>
        <div>
          <h2>{title}</h2>
          {subtitle ? <p>{subtitle}</p> : null}
        </div>
      </header>
      <div className="cvd-review-context__body">{children}</div>
    </section>
  );
}

function InfoTile({ label, value, children }) {
  return (
    <div className="cvd-review-context__tile">
      <span className="cvd-review-context__tile-label">{label}</span>
      <span className="cvd-review-context__tile-value">{children || value || "—"}</span>
    </div>
  );
}

export default function VendorCandidateReviewContextPanels({
  t,
  locale,
  candidate,
  candidateId,
  duplicateMatches = [],
  mapped = {},
  serviciosBase,
  busy,
  hasDuplicateRisk,
  onScanDuplicates,
  onNormalize,
  translateMappedField,
  formatCandidateDate,
}) {
  const importSummary = candidate?.importFilename
    ? t(`${DETAIL_PREFIX}.importSummaryNamed`, {
        file: candidate.importFilename,
        row: candidate.sourceRowNumber ?? "—",
      })
    : t(`${DETAIL_PREFIX}.importSummaryGeneric`);

  const mappedEntries = Object.entries(mapped).filter(([, value]) => value != null && value !== "");

  return (
    <div className="cvd-review-context-stack">
      <ContextSection
        icon="🔍"
        title={
          duplicateMatches.length > 0
            ? t(`${DETAIL_PREFIX}.duplicateSectionTitle`, { count: duplicateMatches.length })
            : t(`${DETAIL_PREFIX}.duplicateSectionTitleEmpty`)
        }
        subtitle={t(`${DETAIL_PREFIX}.duplicateSectionSubtitle`)}
        tone={hasDuplicateRisk ? "warning" : "default"}
      >
        {duplicateMatches.length > 0 ? (
          <ul className="cvd-review-context__match-list">
            {duplicateMatches.map((match) => {
              const peerLabel = getPeerRecordLabel(match, candidateId);
              const peerLink = getPeerRecordLink(match, candidateId, serviciosBase);
              const confidenceLabel = t(
                `${DUPLICATES_PREFIX}.confidence.${match.confidenceBand}`,
                {},
                match.confidenceBand
              );

              return (
                <li key={match.id} className="cvd-review-context__match-card">
                  <div className="cvd-review-context__match-main">
                    <p className="cvd-review-context__match-eyebrow">
                      {matchTypePlainLabel(t, match.matchType)}
                    </p>
                    <h3>
                      {peerLink ? <Link to={peerLink}>{peerLabel}</Link> : peerLabel}
                    </h3>
                    <p className="cvd-review-context__match-copy">
                      {t(`${DETAIL_PREFIX}.duplicateCardHint`, { confidence: confidenceLabel })}
                    </p>
                  </div>
                  <div className="cvd-review-context__match-meta">
                    <span className={`cvd-badge ${confidenceBadgeClass(match.confidenceBand)}`}>
                      {confidenceLabel}
                    </span>
                    <span className="cvd-review-context__match-status">
                      {t(`${DUPLICATES_PREFIX}.status.${match.status}`, {}, match.status)}
                    </span>
                    <Link className="cvd-btn cvd-btn--secondary" to={`${serviciosBase}/duplicados/${match.id}`}>
                      {t(`${DETAIL_PREFIX}.reviewMatchAction`)}
                    </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="cvd-review-context__empty">
            <p>{t(`${DETAIL_PREFIX}.duplicateMatchesEmpty`)}</p>
            <p className="muted">{t(`${DETAIL_PREFIX}.duplicateMatchesEmptyHint`)}</p>
            <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.DUPLICATES_VIEW}>
              <button type="button" className="btn btn-secondary" onClick={onScanDuplicates} disabled={busy}>
                {t(`${DETAIL_PREFIX}.scanDuplicates`)}
              </button>
            </VendorDirectoryPermissionGate>
          </div>
        )}
      </ContextSection>

      <ContextSection
        icon="📥"
        title={t(`${DETAIL_PREFIX}.provenanceTitle`)}
        subtitle={importSummary}
      >
        <div className="cvd-review-context__tiles">
          <InfoTile label={t(`${DETAIL_PREFIX}.source`)} value={candidate?.sourceName || candidate?.sourceId} />
          <InfoTile label={t(`${DETAIL_PREFIX}.import`)}>
            {candidate?.importJobId ? (
              <Link to={`${serviciosBase}/importaciones/${candidate.importJobId}`}>
                {candidate.importFilename || candidate.importJobId}
              </Link>
            ) : (
              "—"
            )}
          </InfoTile>
          <InfoTile label={t(`${DETAIL_PREFIX}.sourceRow`)} value={candidate?.sourceRowNumber} />
          <InfoTile label={t(`${DETAIL_PREFIX}.firstSeen`)} value={formatCandidateDate(candidate?.firstSeenAt, locale)} />
          <InfoTile label={t(`${DETAIL_PREFIX}.lastSeen`)} value={formatCandidateDate(candidate?.lastSeenAt, locale)} />
          {candidate?.sourceRecordId ? (
            <InfoTile label={t(`${DETAIL_PREFIX}.sourceRecordId`)} value={candidate.sourceRecordId} />
          ) : null}
        </div>
      </ContextSection>

      <ContextSection
        icon="✨"
        title={t(`${DETAIL_PREFIX}.cleanupTitle`)}
        subtitle={t(`${DETAIL_PREFIX}.cleanupSubtitle`)}
      >
        <div className="cvd-review-context__tiles">
          <InfoTile label={t(`${DETAIL_PREFIX}.normalizedName`)} value={candidate?.normalizedName} />
          <InfoTile label={t(`${DETAIL_PREFIX}.normalizedPhone`)} value={candidate?.normalizedPhone} />
          <InfoTile label={t(`${DETAIL_PREFIX}.normalizedEmail`)} value={candidate?.normalizedEmail} />
        </div>
        <p className="cvd-review-context__footnote">
          {t(`${DETAIL_PREFIX}.normalizationVersion`)}: {candidate?.normalizationVersion || "—"} ·{" "}
          {t(`${DETAIL_PREFIX}.normalizationLast`)}:{" "}
          {candidate?.normalizedAt ? formatCandidateDate(candidate.normalizedAt, locale) : "—"}
        </p>
        <div className="cvd-review-context__actions">
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.CANDIDATES_UPDATE}>
            <button type="button" className="btn btn-secondary" onClick={onNormalize} disabled={busy}>
              {t(`${DETAIL_PREFIX}.renormalize`)}
            </button>
          </VendorDirectoryPermissionGate>
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.DUPLICATES_VIEW}>
            <button type="button" className="btn btn-secondary" onClick={onScanDuplicates} disabled={busy}>
              {t(`${DETAIL_PREFIX}.scanDuplicates`)}
            </button>
            <Link className="btn btn-secondary" to={`${serviciosBase}/duplicados?status=pending`}>
              {t(`${DETAIL_PREFIX}.openDuplicatesQueue`)}
            </Link>
          </VendorDirectoryPermissionGate>
        </div>
      </ContextSection>

      {mappedEntries.length > 0 ? (
        <ContextSection
          icon="📋"
          title={t(`${DETAIL_PREFIX}.mappedDataTitle`)}
          subtitle={t(`${DETAIL_PREFIX}.mappedDataSubtitle`)}
        >
          <div className="cvd-review-context__tiles">
            {mappedEntries.map(([key, value]) => (
              <InfoTile key={key} label={translateMappedField(key)} value={value} />
            ))}
          </div>
        </ContextSection>
      ) : null}

      {candidate?.validationWarnings?.length > 0 ? (
        <ContextSection
          icon="⚠️"
          title={t(`${DETAIL_PREFIX}.warningsTitle`)}
          subtitle={t(`${DETAIL_PREFIX}.warningsSubtitle`)}
          tone="warning"
        >
          <ul className="cvd-review-context__warning-list">
            {candidate.validationWarnings.map((warning, index) => (
              <li key={index}>
                {typeof warning === "string" ? warning : warning.message || JSON.stringify(warning)}
              </li>
            ))}
          </ul>
        </ContextSection>
      ) : null}

      {candidate?.rawPayload ? (
        <details className="cvd-technical-details">
          <summary>
            <span className="cvd-technical-details__title">{t(`${DETAIL_PREFIX}.technicalDetails`)}</span>
            <span className="cvd-technical-details__hint">{t(`${DETAIL_PREFIX}.technicalDetailsHint`)}</span>
          </summary>
          <pre className="cvd-technical-details__payload">
            {JSON.stringify(candidate.rawPayload, null, 2)}
          </pre>
        </details>
      ) : null}
    </div>
  );
}
